import Grid from '@mui/material/Grid';
import { errorMessage, LOCAL_STORAGE_KEYS } from 'constants/CommonConstants';
import { formPatternRules, formRequiredRules } from 'helper';
import React, { useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { CommonInput, SelectInput } from 'sharedComponents/inputGroup';
import { CommonPhoneInput } from 'sharedComponents/inputGroup/CommonPhoneInput';
import Bulb from '../icons/bulb.svg';
import { PROVINCE } from './province';
import VehicleDetails from './sub-component/VehicleDetails';
import { EMAIL_REGEX, PHONE_REGEX } from 'constants/Regex';
import { postLeadCreation } from 'store/LeadCreationState';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { useDispatch, useSelector } from 'react-redux';
import { getIpv4Data } from 'store/CommonModuleState';
import { Lead } from 'pages/leadGen/LeadGenInterface';
import { PAYMENT_METHOD } from '../DigitalRetailingConstant';

const Step1: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext }, ref) => {
  const { t } = useTranslation();

  const hasCalledNextStep = React.useRef(false);

  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const { authenticateClientData } = useSelector((state: RootState) => state.auth);

  const leadSourceId = authenticateClientData?.data?.widgetData?.leadSourceId;

  const widgetData = authenticateClientData?.data?.widgetData || {};

  // React Hook Form
  const { control, trigger, watch, getValues } = useFormContext();

  const provinceWatcher = watch('province');
  const payTypeWatcher = watch('payType');

  // React Hook Form Trigger
  const nextStep = async () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    let IPv4: string = '';

    try {
      // Create a timeout promise that rejects after a certain time
      const timeout = new Promise(
        (_, reject) => setTimeout(() => reject(new Error('Request timed out')), 2000) // 3 seconds timeout
      );

      // Attempt to fetch IPv4 address with timeout
      IPv4 = await Promise.race([getIpv4Data(), timeout]);
    } catch (error) {
      console.error('Error fetching IPv4 data:', error?.message);
      // Handle the error as needed (e.g., continue with default IPv4)
    }

    const { firstName, lastName, email, phone, postalcode, province } = getValues();

    const personalInfoData: Lead = {
      firstName,
      lastName,
      email,
      phone,
      leadSourceId,
      province: province,
      zipCode: postalcode,
      leadDetails: {
        'IP Address': IPv4,
        'Postal Code': postalcode,
        Province: province,
      },
    };

    console.log(personalInfoData);

    dispatch(postLeadCreation({ data: personalInfoData, leadTypeID: widgetData?.leadTypeId })).then((res) => {
      if (res?.payload?.success && (res?.payload?.data?.draftLeadId || res?.payload?.data?._id)) {
        res?.payload?.data?.draftLeadId &&
          localStorage.setItem(LOCAL_STORAGE_KEYS.draftLeadId, res?.payload?.data?.draftLeadId);
        onNext?.();
      }
    });

    // onNext?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
  }));

  return (
    <div className='dr-widget-steps with-sidebar step-1'>
      <div className='dr-widget-steps__content'>
        <h2 className='dr-widget-steps__title'>{t('digitalRetailing.tellUsAboutYourself')}</h2>
        <p className='dr-widget-steps__step-note text-secondary'>{t('digitalRetailing.provideInfo')}</p>
        <div className='dr-widget-steps__form-wrapper'>
          <Grid container spacing={2}>
            <Grid item sm={6} xs={12}>
              <CommonInput
                name='firstName'
                control={control}
                label={t('stepperForm.firstName')}
                placeholder={t('stepperForm.enterFirstName')}
                triggerValidationForField={trigger}
                rules={formRequiredRules(errorMessage(t).firstNameRequired)}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <CommonInput
                name='lastName'
                control={control}
                label={t('stepperForm.lastName')}
                placeholder={t('stepperForm.enterLastName')}
                triggerValidationForField={trigger}
                rules={formRequiredRules(errorMessage(t).lastNameRequired)}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <CommonInput
                name='email'
                control={control}
                label={t('stepperForm.emailEg')}
                placeholder={t('stepperForm.enterEmail')}
                triggerValidationForField={trigger}
                rules={{
                  ...formRequiredRules(errorMessage(t).emailRequired),
                  ...formPatternRules(EMAIL_REGEX, errorMessage(t).invalidEmailFormat),
                }}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <CommonPhoneInput
                name='phone'
                control={control}
                label={t('stepperForm.phone')}
                placeholder={t('stepperForm.enterNumber')}
                triggerValidationForField={trigger}
                rules={{
                  ...formRequiredRules(errorMessage(t).phoneRequired),
                  ...formPatternRules(PHONE_REGEX, errorMessage(t).invalidPhoneFormat),
                }}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <SelectInput
                name='province'
                control={control}
                label={t('stepperForm.province')}
                rules={formRequiredRules(errorMessage(t).provinceRequired)}
                options={PROVINCE}
                triggerValidationForField={trigger}
                defaultValue={provinceWatcher}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <CommonInput
                name='postalcode'
                control={control}
                label={t('stepperForm.postalCode')}
                placeholder={t('stepperForm.enterPostalCode')}
                triggerValidationForField={trigger}
                rules={formRequiredRules(errorMessage(t).postalRequired)}
                maxLength={6}
              />
            </Grid>
            <Grid item xs={12}>
              <SelectInput
                name='payType'
                control={control}
                label={t('digitalRetailing.paymentMethod')}
                options={PAYMENT_METHOD(t)}
                triggerValidationForField={trigger}
                rules={formRequiredRules(errorMessage(t).required)}
                defaultValue={payTypeWatcher}
              />
            </Grid>
          </Grid>
        </div>
        <div className='dr-widget-note with-icon text-secondary'>
          <span className='dr-widget-note__icon'>
            <img src={Bulb} alt='Bulb' />
          </span>
          {t('digitalRetailing.note1')}
        </div>
      </div>
      <VehicleDetails />
    </div>
  );
};

const StepperForm1 = React.forwardRef(Step1);

export default StepperForm1;
